<template>
    <tile class="opc-zsched">
      <div v-if="alertMsg">
        <zm-banner class="opc-zsched-top__alert" type="info" show-close>
          <span v-html="alertMsg" />
        </zm-banner>
      </div>
      <addon-info-banner
        v-if="shouldShowAddonInfoBanner"
        :plan-name="$t('billing.scheduler.short_title')"
        :base-plan-name="basePlanName"
      />
      <foldable v-model="isExpanded" :title="title" :isRemoveWrapAriaLabelledby="true">
        <template #head>
        <CardHeader :logo="logo" :title="title">
          <template #head-right-content>
            <!-- Teleport target container -->
            <TeleportTarget
              :id="planQuantityTeleportId"
              :visible="showCustomCollapseContent"
              ref="teleportTarget"
            />
            <div class="opc-zm-base-card__view-detail-button" v-if="isExpanded && showPlanDetail">
              <zm-button data-testid="checkout-button-247" class="opc-zm-base-card__link" type="link" @click="handlePlanDetailsClick">
                {{ $t('billing.plan_details') }}
              </zm-button>
            </div>
          </template>
          <div v-html="getPlanCollapseText()" class="opc-zm-base-card__collapse opc-zm-base-card__collapse--reset" v-if="!isExpanded && formPlan">
          </div>
        </CardHeader>
        </template>
        <div class="opc-zsched__main">
          <zm-form ref="zmoneForm" label-position="top" class="opc-config-form" @submit.prevent>
            <zm-form-item :label="$t('billing.zone.select_license')">
              <users-alert :maxUsers="maxHost" :selectedUsers="form.selectedNumber" />
              <InputNumberWithPopover
                class="opc-zsched__quantity-selector"
                v-model="form.selectedNumber"
                size="lg"
                controls-position="both"
                :min="minHost"
                :max="maxHost"
                @change="submitForm"
                :decrease-tips="decreaseTips"
                :label="title + ' Select the number of users'"
                :teleport-target="planQuantityTeleportTarget"
                :data-testid="`checkout-input-license-${formPlan ? formPlan.plan_code : ''}`"
              />
              <bought-license v-if="isEditFlow" :type="planType"/>
              <zm-banner
                class="reason-tips"
                v-if="licenseTips"
                type="info"
                show-close
              >
                <p v-html="licenseTips"></p>
              </zm-banner>
              <div class="opc-zsched__exceed-assigned-licenses-hint" v-if="mx_downSellLicenseErrorMessage">
                <div class="opc-zsched__exceed-assigned-licenses-hint-icon">
                  <zm-icon><AlertTriangleFillColor /></zm-icon>
                </div>
                <p class="opc-zsched__exceed-assigned-licenses-hint-text">{{ mx_downSellLicenseErrorMessage }}</p>
              </div>
            </zm-form-item>
            <zm-form-item :label="$t('billing.select_billing_cycle')" id="opc-zsched__billing-cycle">
              <billed-billing-cycle-radio
                v-model="form.billingCycle"
                :billingCycleList="billingCycleList"
                @change="updateBillingCycle"
              />
            </zm-form-item>
          </zm-form>
          <basic-related-tip v-if="isNetNew && !shouldShowAddonInfoBanner" />
          <div class="opc-zm-base-card__main__footer" v-if="couldBeRemovedAsAddonCard">
            <zm-button :loading="planCardState.removeLoading" data-testid="checkout-button-249" type="link" ghost @click="removePlanByKey" :aria-label="'remove ' + title + ' plan'">{{ $t('billing.common.remove') }}</zm-button>
          </div>
          <preview-payment class="opc-zsched__preview-payment" />
        </div>
      </foldable>
    </tile>
</template>
<script>
import Foldable from '@/components/foldable.vue';
import { AlertTriangleFillColor } from '@zoom/icons-vue';
import Tile from '@/components/tile.vue';
import baseFormMixin from "@/plans/ZOOM_SCHEDULER/mx/base";
import formMixin from "@/plans/ZOOM_SCHEDULER/mx/index";
import InputNumberWithPopover from '@/components/input-number/input-number-with-popover';
import UsersAlert from '../components/alerts.vue';
import saveMixins from '../../ZOOM_MEETING/save-mixins';
import { BillingCycleEnum, planTypes } from '@/common/constants';
import PreviewPayment from '@/views/checkout/payment/preview-payment';
import BasicRelatedTip from '../components/basic-related-tip.vue';
import { $context } from '@/global-install';
import BoughtLicense from '@/views/config/shopping-cart/prorated/bought-license.vue';
import BilledBillingCycleRadio from "@/components/service/config-billing-cycle/billed-cycle/index.vue";
import { getPlanCollapseText } from '@/common/product';
import CardHeader from '@/components/plan-card-header.vue';
import { plansof } from '@/utils/chain';
import { mapGetters } from 'vuex';
import teleportTargetMixin from '@/components/teleport-target/mixin';
import AddonInfoBanner from '@/components/addon-info-banner.vue';
import { shouldShowAddonInfoBanner, getBasePlanName } from '@/utils/addon-info-banner';

export default {
  key: planTypes[planTypes.ZOOM_SCHEDULER],
  mounted() {
    this.$context.$on('collapse-plan-card', this.collapseCard);
  },
  methods: {
    BillingCycleEnum() {
      return BillingCycleEnum
    },
    collapseCard() {
      this.isExpanded = false;
    },
    getPlanCollapseText() {
      return getPlanCollapseText({
        amount: this.formPlan?.amount,
        billingCycle: this.form.billingCycle,
        quantity: this.form?.selectedNumber,
        cycleValue: this.formPlan?.cycleValue,
        unit: this.formPlan?.unit,
      });
    },
  },
  mixins: [baseFormMixin, formMixin, saveMixins, teleportTargetMixin],
  components: {
    PreviewPayment,
    Foldable,
    Tile,
    InputNumberWithPopover,
    UsersAlert,
    BasicRelatedTip,
    BoughtLicense,
    BilledBillingCycleRadio,
    CardHeader,
    AddonInfoBanner,
    AlertTriangleFillColor
  },
  computed: {
    ...mapGetters(['getSelectedPlans', 'getCurrentBasePlanInfo', 'boughtPlans']),
    alertMsg() {
      return "";
    },
    isNetNew() {
      return $context.is.buyNow || $context.is.buyTrial || $context.is.basic2pro;
    },
    isEditFlow() {
      return $context.is.edit;
    },
    // Addon info banner logic
    shouldShowAddonInfoBanner() {
      return shouldShowAddonInfoBanner(
        this.getCurrentBasePlanInfo,
        this.getSelectedPlans,
        this.boughtPlans,
        planTypes.ZOOM_SCHEDULER,
        $context
      );
    },
    basePlanName() {
      return getBasePlanName(
        this.getCurrentBasePlanInfo,
        this.getSelectedPlans,
        this.boughtPlans,
        planTypes.ZOOM_SCHEDULER,
        this.$t
      );
    },
    /** soft bundle custom collapse content start */
    showCustomCollapseContent() {
      const selectedPlans = plansof(this.getSelectedPlans).type(planTypes.ZOOM_SCHEDULER).get();
      return this.soft_bundle_showCustomCollapseContent && !this.isExpanded && selectedPlans?.length === 1;
    },
    /** soft bundle custom collapse content end */
  },
};
</script>

<style lang="scss">
@import '@/styles/_variables.scss';
@import '@/styles/zoom-card.scss';

.opc-zsched {
  .zbo-form-item {
    &__label-wrapper {
      margin-bottom: 0px;
    }
  }

  .opc-zsched-top__alert {
    display: flex;
    padding: 16px;
    align-items: center;
    gap: 16px;
    border-radius: 8px;
    background: #F7F7F8;
    margin-bottom: 24px;

    .zbo-banner__body {
      padding: 0;
      color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
      font-size: 14px;
      font-style: normal;
      font-weight: 450;
      line-height: 150%;
    }
  }
  .main-icon {
    color: $theme-color;
  }
    &__link.zbo-button--text {
    font-weight: 700 !important;
    color: $theme-color;
    padding: 0;
  }
  &__quantity-selector {
    margin-top: 8px;
  }
  &__main {
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid var(--zoom-color-border-subtle-neutral, #DFE3E8);
  }
  &__desc {
    position: relative;
    font-style: normal;
    font-weight: 500;
    font-size: 14px;
    line-height: 150%;
    color: var(--zoom-color-text-strong-neutral, #555B62);
    padding: 0;
    &.is-warning {
      padding: 8px 12px 8px 12px;
      background: var(--zoom-color-fill-subtler-warning, #FFF9F2);
      border-radius: 8px;
    }
  }
  &__exceed-assigned-licenses-hint {
    margin-top: 12px;
    display: flex;
    padding: 8px 17px;
    align-items: center;
    background: var(--zoom-color-bg-neutral, #FCFDFD);
    border-radius: 8px;
    &-icon {
      color: var(--zoom-color-state-disable, #ADB1B8);
      width: 14px;
    }
    &-text {
      margin-left: 13px;
      font-size: 14px;
      line-height: 1.5;
      color: var(--zoom-color-text-strong-neutral, #555B62);
    }

  }
  .opc-card-radio {
    margin-top: 8px;

    .opc-zsched__radio {
      width: 100%;
      .zbo-radio__wrap {
        width: 100%;
      }
    }
  }
  .opc-card-radio &__radio {
    .zbo-radio__label--inner {
      width: 100%;
      min-height: 90px;
      padding: 16px;
    }
    &-title {
      font-style: normal;
      font-weight: 600;
      font-size: 16px;
      line-height: 150%;
      color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
      margin: 0;
      display: flex;
      align-items: center;
    }
    &-price {
      font-style: normal;
      word-break: break-word;
      color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
      &-amount {
          font-weight: 590;
          font-size: 24px;
          line-height: 28px;
      }
      &-unit {
          font-weight: 400;
          font-size: 14px;
          line-height: 18px;
      }
      &-delete {
          font-weight: 400;
        font-size: 14px;
          line-height: 18px;
        text-decoration-line: line-through;
        color: var(--zoom-color-text-strong-neutral, #555B62);
      }
    }
  }

  &__preview-payment {
    margin-top: 24px;
  }

}
.reason-tips {
  margin-top: 12px;

  p {
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    line-height: 150%;
    color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
  }
}
</style>
